
import { supabase } from "@/integrations/supabase/client";
import { logActivityEvent } from "@/services/activity/activityLogService";

/**
 * Request payment for an order
 * @param orderId - The order ID to request payment for
 * @param tableId - The table ID associated with the order
 * @param storeId - The store ID associated with the order
 * @returns Promise with success status
 */
export const requestPayment = async (
  orderId: string,
  tableId: string,
  storeId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get table information first
    const { data: tableData, error: tableError } = await supabase
      .from("store_tables")
      .select("number, name")
      .eq("id", tableId)
      .single();

    if (tableError) {
      console.error("Error fetching table information:", tableError);
    }

    const tableNumber = tableData?.number;
    const tableName = tableData?.name;

    // Create the payment request
    const { data, error } = await supabase
      .from("payment_requests")
      .insert({
        order_id: orderId,
        table_id: tableId,
        store_id: storeId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating payment request:", error);
      return { success: false, error: error.message };
    }

    // Log the activity using the activity logs table directly
    try {
      await logActivityEvent({
        entity_type: "payment_request",
        entity_id: data.id,
        action: `Table ${tableNumber} (${tableName}) Payment Request`,
        user_name: "Customer",
        store_id: storeId,
        details: {
          order_id: orderId,
          table_id: tableId,
          table_number: tableNumber,
          table_name: tableName
        },
      });
    } catch (activityError) {
      console.error("Error logging payment request activity:", activityError);
      // Continue even if activity logging fails
    }

    return { success: true };
  } catch (error) {
    console.error("Error in requestPayment:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error requesting payment",
    };
  }
};
